import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Play, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight } from 'lucide-react';
import OrderingTab from './OrderingTab';
import SupportTab from './SupportTab';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('features');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const products = {
    RUT956: {
      // Existing RUT956 data remains unchanged
      name: 'RUT956',
      image: '/product.png',
      images: [
        '/product.png',
        '/rut956-2.png',
        '/rut956-3.png',
        '/rut956-4.png'
      ],
      video: 'https://youtu.be/n1N6q4051vI',
      description: 'Advanced 4G/LTE router with enhanced security features and industrial-grade connectivity.',
      features: ['4G/LTE (Cat 4)', '3G, 2G', 'IP55', 'PoE-In/Out'],
      specifications: {
        // Mobile
        'Mobile module': '4G LTE Cat 4 up to 150 DL/50 UL Mbps; 3G up to 21 DL/5.76 UL Mbps; 2G up to 236.8 DL/236.8 UL kbps',
        '3GPP Release': 'Release 11',
        'SIM switch': '2 SIM cards, auto-switch cases: weak signal, data limit, SMS limit, roaming, no network, network denied, data connection fail, SIM idle protection',
        'Mobile Status': 'IMSI, ICCID, operator, operator state, data connection state, network type, bandwidth, connected band, signal strength (RSSI), SINR, RSRP, RSRQ, EC/IO, RSCP, data sent/received, LAC, TAC, cell ID, ARFCN, UARFCN, EARFCN, MCC, and MNC',
    
        // SMS
        'SMS Capabilities': 'SMS status, SMS configuration, send/read SMS via HTTP POST/GET, EMAIL to SMS, SMS to EMAIL, SMS to HTTP, SMS to SMS, scheduled SMS, SMS autoreply, SMPP',
    
        // USSD
        'USSD Support': 'Supports sending and reading Unstructured Supplementary Service Data messages',
    
        // Black/White list
        'Operator Black/White List': 'Operator black/white list (by country or separate operators)',
    
        // Multiple PDN
        'Multiple PDN': 'Possibility to use different PDNs for multiple network access and services',
    
        // Band management
        'Band Management': 'Band lock, Used band status display',
    
        // SIM PIN Code Management
        'SIM PIN Code Management': 'Setting, changing, or disabling the SIM card PIN',
    
        // APN
        'APN': 'Auto APN',
    
        // Bridge
        'Bridge': 'Direct connection (bridge) between mobile ISP and device on LAN',
    
        // Passthrough
        'Passthrough': 'Router assigns its mobile WAN IP address to another device on LAN',
    
        // Wireless
        'Wireless Mode': '802.11b/g/n (Wi-Fi 4), Access Point (AP), Station (STA)',
        'Wi-Fi Security': 'WPA2-Enterprise: PEAP, WPA2-PSK, WPA-EAP, WPA-PSK, WPA3-SAE, WPA3-EAP, OWE; AES-CCMP, TKIP, Auto-cipher modes, client separation, EAP-TLS with PKCS#12 certificates, disable auto-reconnect, 802.11w Protected Management Frames (PMF)',
        'SSID/ESSID': 'SSID stealth mode and access control based on MAC address',
        'Wi-Fi Users': 'Up to 100 simultaneous connections',
        'Wireless Connectivity Features': 'Fast roaming (802.11r), Relayd, BSS transition management (802.11v), radio resource measurement (802.11k)',
        'Wireless MAC Filter': 'Allowlist, blocklist',
        'Wireless QR Code Generator': 'Once scanned, a user will automatically enter your network without needing to input login information',
        'TravelMate': 'Forward Wi-Fi hotspot landing page to a subsequent connected device',
    
        // Network
        'Hotspot': 'Captive portal (hotspot), internal/external Radius server, Radius MAC authentication, SMS authorisation, SSO authentication, internal/external landing page, walled garden, user scripts, URL parameters, user groups, individual user or group limitations, user management, 9 default customisable themes and optionality to upload and download customised hotspot themes',
        'Routing': 'Static routing, Dynamic routing (BGP, OSPF v2, RIP v1/v2, EIGRP, NHRP), Policy based routing',
        'Network Protocols': 'TCP, UDP, IPv4, IPv6, ICMP, NTP, DNS, HTTP, HTTPS, SFTP, FTP, SMTP, SSL/TLS, ARP, VRRP, PPP, PPPoE, UPNP, SSH, DHCP, Telnet, SMPP, SNMP, MQTT, Wake On Lan (WOL), VXLAN',
        'VoIP Passthrough Support': 'H.323 and SIP-alg protocol NAT helpers, allowing proper routing of VoIP packets',
        'Connection Monitoring': 'Ping Reboot, Wget Reboot, Periodic Reboot, LCP and ICMP for link inspection',
        'Firewall': 'Port forward, traffic rules, custom rules, TTL target customisation',
        'DHCP': 'Static and dynamic IP allocation, DHCP relay, DHCP server configuration, status, static leases: MAC with wildcards',
        'QoS / Smart Queue Management (SQM)': 'Traffic priority queuing by source/destination, service, protocol or port, WMM, 802.11e',
        'DDNS': 'Supported >25 service providers, others can be configured manually',
        'DNS over HTTPS': 'DNS over HTTPS proxy enables secure DNS resolution by routing DNS queries over HTTPS',
        'Network Backup': 'Wi-Fi WAN, Mobile, VRRP, Wired options, each of which can be used as an automatic Failover',
        'Load Balancing': 'Balance Internet traffic over multiple WAN connections',
        'SSHFS': 'Possibility to mount remote file system via SSH protocol',
        'Traffic Management': 'Real-time monitoring, wireless signal charts, traffic usage history',
    
        // Ethernet
        'WAN': '1 x WAN port 10/100 Mbps, compliance IEEE 802.3, IEEE 802.3u, 802.3az standards, supports auto MDI/MDIX',
        'LAN': '3 x LAN ports, 10/100 Mbps, compliance IEEE 802.3, IEEE 802.3u standards, supports auto MDI/MDIX',
    
        // Security
        'Authentication': 'Pre-shared key, digital certificates, X.509 certificates, TACACS+, Internal & External RADIUS users authentication, IP & login attempts block, time-based login blocking, built-in random password generator',
        'Firewall': 'Preconfigured firewall rules can be enabled via WebUI, unlimited firewall configuration via CLI, DMZ, NAT, NAT-T, NAT64',
        'Attack Prevention': 'DDOS prevention (SYN flood protection, SSH attack prevention, HTTP/HTTPS attack prevention), port scan prevention (SYN-FIN, SYN-RST, X-mas, NULL flags, FIN scan attacks)',
        'VLAN': 'Port and tag-based VLAN separation',
        'Mobile Quota Control': 'Mobile data limit, customizable period, start time, warning limit, phone number',
        'WEB Filter': 'Blacklist for blocking out unwanted websites, Whitelist for specifying allowed sites only',
        'Access Control': 'Flexible access control of SSH, Web interface, CLI and Telnet',
        'SSL Certificate Generation': "Let's encrypt support",
    
        // Add additional sections as required...
    }
    ,
      downloads: {
        manualDocs: [
          {
            title: 'RUT956 FLYER (EN)',
            updateDate: '2024-08-07',
            type: 'flyer',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2022/12/63a031f9d449e5-38028829/datasheet/719553-rut956-flyer-2024-v11.pdf'
          },
          {
            title: 'RUT956 DATASHEET (EN)',
            updateDate: '2024-04-09',
            type: 'datasheet',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2022/12/63a031f9d449e5-38028829/datasheet/717017-rut956-datasheet-2024-v11.pdf'
          }
        ],
        links: [
          {
            title: 'RUT956 QUICK START GUIDE (EN)',
            type: 'guide',
            url: 'https://teltonika-networks.com/QSG_RUT956'
          },
          {
            title: 'FIRMWARE DOWNLOADS',
            type: 'firmware',
            url: 'https://wiki.teltonika-networks.com/view/RUT956_Firmware_Downloads'
          },
          {
            title: 'RUT241 USER MANUAL',
            type: 'manual',
            url: 'https://wiki.teltonika-networks.com/view/RUT956_Manual'
          },
          {
            title: 'PRODUCT CHANGE NOTIFICATIONS',
            type: 'certifications',
            url: 'https://wiki.teltonika-networks.com/view/RUT956_Product_Change_Notifications'
          },
          {
            title: 'CERTIFICATIONS & APPROVALS',
            type: 'certifications',
            url: 'https://wiki.teltonika-networks.com/view/RUT956_Certification_%26_Approvals'
          },
          {
            title: 'SDK DOWNLOADS',
            type: 'sdk',
            url: 'https://wiki.teltonika-networks.com/view/Software_Development_Kit'
          },
          {
            title: 'GPL DOWNLOADS',
            type: 'gpl',
            url: 'https://wiki.teltonika-networks.com/view/OSS'
          },
          {
            title: 'RUT956 3D MODELS',
            type: '3d',
            url: 'https://wiki.teltonika-networks.com/view/RUT956_Spatial_Measurements_%26_Weight#3D_view'
          }
        ]
      }
    },
    RUT241: {
      name: 'RUT241',
      image: '/rut241-1.png',
      images: [
        '/rut241-1.png',
        '/rut241-2.png',
        '/rut241-3.png',
        '/rut241-4.png'
      ],
      video: 'https://youtu.be/H4Qvq6ZJdNQ',
      description: 'Professional cellular router for industrial applications.',
      features: [
        '4G LTE Cat 4',
        'Wi-Fi 802.11b/g/n',
        'Dual Ethernet ports',
        'Digital I/O'
      ],
      specifications: {
        // Mobile
        'Mobile module': '4G LTE Cat 4 up to 150 DL/50 UL Mbps; 3G up to 21 DL/5.76 UL Mbps; 2G up to 236.8 DL/236.8 UL kbps',
        '3GPP Release': 'Release 10/11 depending on the hardware version',
        'Mobile Status': 'IMSI, ICCID, operator, operator state, data connection state, network type, bandwidth, connected band, signal strength (RSSI), SINR, RSRP, RSRQ, EC/IO, RSCP, data sent/received, LAC, TAC, cell ID, ARFCN, UARFCN, EARFCN, MCC, and MNC',
        
        // Wireless
        'Wireless Mode': '802.11b/g/n (Wi-Fi 4), Access Point (AP), Station (STA)',
        'Wi-Fi Security': 'WPA2-Enterprise: PEAP, WPA2-PSK, WPA-EAP, WPA-PSK, WPA3-SAE, WPA3-EAP, OWE',
        'Wi-Fi Users': 'Up to 50 simultaneous connections',
        
        // Ethernet
        'WAN': '1 x WAN port 10/100 Mbps, compliance IEEE 802.3, IEEE 802.3u, 802.3az standards',
        'LAN': '1 x LAN port, 10/100 Mbps, compliance with IEEE 802.3, IEEE 802.3u standards',
        
        // Input/Output
        'Input': '1 x Digital Input, 0 - 6 V detected as logic low, 8 - 30 V detected as logic high',
        'Output': '1 x Digital Output, Open collector output, max output 30 V, 300 mA',
        
        // Power
        'Input Voltage': '9 – 30 VDC, reverse polarity protection',
        'Power Consumption': '< 6.5 W Max',
        'PoE': 'Passive PoE over spare pairs, Mode B, 9 - 30 VDC',
        
        // Physical
        'Dimensions': '83 x 25 x 74 mm',
        'Weight': '125g',
        'Casing': 'Aluminium housing, plastic panels',
        'Operating Temperature': '-40°C to 75°C',
        'Operating Humidity': '10% to 90% non-condensing',
        'Protection Rating': 'IP30',
        
        // Interfaces
        'SIM': '1 x SIM slot (Mini SIM – 2FF), 1.8 V/3 V, external SIM holder',
        'Antennas': '2 x SMA for LTE, 1 x RP-SMA for Wi-Fi antenna connectors',
        'Status Indicators': '3 x Connection type status LEDs, 5 x Connection strength LEDs, 2 x LAN status LEDs, 1 x Power LED',
        
        // System
        'CPU': 'Mediatek, 580 MHz, MIPS 24KEc',
        'RAM': '128 MB, DDR2',
        'Storage': '16 MB, SPI Flash',
        'Operating System': 'RutOS (OpenWrt based Linux OS)'
      },
      downloads: {
        manualDocs: [
          {
            title: 'RUT241 FLYER (EN)',
            updateDate: '2024-10-23',
            type: 'flyer',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2023/01/63b7fa877dd3c0-93872907/flyer/rut241-flyer-2023-v11.pdf'
          },
          {
            title: 'RUT241 DATASHEET (EN)',
            updateDate: '2024-11-06',
            type: 'datasheet',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2023/01/63b7fa877dd3c0-93872907/datasheet/rut241-datasheet-2024-v11.pdf'
          }
        ],
        links: [
          {
            title: 'RUT241 QUICK START GUIDE (EN)',
            type: 'guide',
            url: 'https://wiki.teltonika-networks.com/view/QSG_RUT241'
          },
          {
            title: 'FIRMWARE DOWNLOADS',
            type: 'firmware',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Firmware_Downloads'
          },
          {
            title: 'RUT241 USER MANUAL',
            type: 'manual',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Manual'
          },
          {
            title: 'PRODUCT CHANGE NOTIFICATIONS',
            type: 'certifications',
            url: 'https://wiki.teltonika-networks.com/view/Product_Change_Notifications'
          },
          {
            title: 'CERTIFICATIONS & APPROVALS',
            type: 'certifications',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Certification_%26_Approvals'
          },
          {
            title: 'SDK DOWNLOADS',
            type: 'sdk',
            url: 'https://wiki.teltonika-networks.com/view/Software_Development_Kit'
          },
          {
            title: 'GPL DOWNLOADS',
            type: 'gpl',
            url: 'https://wiki.teltonika-networks.com/view/GPL'
          },
          {
            title: 'RUT241 3D MODELS',
            type: '3d',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Spatial_Measurements_%26_Weight#3D_view'
          }
        ]
      }
    },
    // Rest of the products remain unchanged
    RUTm50: {
      name: 'RUTm50',
      image: '/rutm50-5.png',
      images: [
        '/rutm50-5.png',
        '/rutm50-4.png',
        '/rutm50-2.png',
        '/rutm50-6.png'
      ],
      video: 'https://youtu.be/6iAanISoKZs',
      description: 'High-performance industrial cellular router.',
      features: ['4G LTE', 'Wi-Fi', 'GPS', 'USB'],
      specifications: {
        // Mobile
        'Mobile Module': '5G Sub-6 GHz SA, NSA; 4G LTE Cat 19 up to 1.6Gbps DL / Cat 18 200Mbps UL; 3G up to 21 Mbps DL / 5.76 Mbps UL',
        '3GPP Release': 'Release 16',
        'SIM Switch': 'Auto-switch cases: weak signal, data limit, SMS limit, roaming, no network, network denied, data connection fail, SIM idle protection',
        'Mobile Status': 'IMSI, ICCID, operator, operator state, data connection state, network type, CA indicator, bandwidth, connected band, RSSI, SINR, RSRP, RSRQ, EC/IO, RSCP, data sent/received, LAC, TAC, cell ID, ARFCN, UARFCN, EARFCN, MCC, MNC',
    
        // SMS
        'SMS Capabilities': 'SMS status, SMS configuration, send/read SMS via HTTP POST/GET, EMAIL to SMS, SMS to EMAIL, SMS to HTTP, SMS to SMS, scheduled SMS, SMS autoreply, SMPP',
    
        // USSD
        'USSD Support': 'Supports sending and reading Unstructured Supplementary Service Data (USSD)',
    
        // Black/White List
        'Operator Black/White List': 'Support for country or operator-specific black/white lists',
    
        // Multiple PDN
        'Multiple PDN': 'Ability to use different PDNs for multiple network access and services',
    
        // Band Management
        'Band Management': 'Band lock, used band status display',
    
        // SIM PIN Code Management
        'SIM PIN Code Management': 'Set, change, or disable SIM PIN code',
    
        // APN
        'APN': 'Auto APN',
    
        // Bridge
        'Bridge': 'Direct connection between mobile ISP and LAN device',
        'Passthrough': 'Router assigns mobile WAN IP to LAN device',
    
        // Wireless
        'Wireless Mode': '802.11b/g/n/ac Wave 2 (Wi-Fi 5), Dual Band, MU-MIMO',
        'Wi-Fi Security': 'WPA2-Enterprise (PEAP), WPA2-PSK, WPA-EAP, WPA-PSK, WPA3-SAE, WPA3-EAP, OWE; AES-CCMP, TKIP, Auto-cipher modes, client separation, EAP-TLS with PKCS#12 certificates, 802.11w PMF',
        'SSID/ESSID': 'ESSID stealth mode',
        'Wi-Fi Users': 'Up to 150 simultaneous connections',
        'Wireless Connectivity Features': 'Wireless mesh (802.11s), Fast roaming (802.11r), Relayd, BSS transition management (802.11v), Radio resource measurement (802.11k)',
        'Wireless MAC Filter': 'Allowlist, blocklist',
        'Wireless QR Code Generator': 'Scanned code automatically connects users',
        'TravelMate': 'Forward Wi-Fi hotspot landing page to connected device',
    
        // Ethernet
        'WAN': '1 x WAN port, 10/100/1000 Mbps, compliance IEEE 802.3, IEEE 802.3u, 802.3az, auto MDI/MDIX',
        'LAN': '4 x LAN ports, 10/100/1000 Mbps, compliance IEEE 802.3, IEEE 802.3u, 802.3az, auto MDI/MDIX',
    
        // Security
        'Authentication': 'Pre-shared key, digital certificates, X.509 certificates, TACACS+, RADIUS authentication, IP & login attempts block, time-based login blocking',
        'Firewall': 'Preconfigured firewall rules, DMZ, NAT, NAT-T, NAT64, unlimited firewall configuration via CLI',
        'Attack Prevention': 'DDOS prevention (SYN flood, SSH attack, HTTP/HTTPS attack), port scan prevention (SYN-FIN, SYN-RST, X-mas, NULL flags, FIN scan)',
        'VLAN': 'Port and tag-based VLAN separation',
        'Mobile Quota Control': 'Customizable data limit, period, start time, warning limit',
        'WEB Filter': 'Blacklist and whitelist for websites',
        'Access Control': 'Access control for SSH, Web interface, CLI, and Telnet',
        'SSL Certificate Generation': "Let's Encrypt support",
    
        // Additional sections...
        'Network Protocols': 'TCP, UDP, IPv4, IPv6, ICMP, NTP, DNS, HTTP, HTTPS, SFTP, FTP, SMTP, SSL/TLS, ARP, VRRP, PPP, PPPoE, UPNP, SSH, DHCP, Telnet, SNMP, MQTT',
        'Traffic Management': 'Real-time monitoring, traffic usage history, wireless signal charts',
        'QoS': 'Traffic priority by source/destination, protocol, WMM, 802.11e',
        'Load Balancing': 'Balance traffic over multiple WAN connections',
        'Network Backup': 'Wi-Fi WAN, Mobile, VRRP, Wired options as Failover',
        'DNS over HTTPS': 'Secure DNS resolution via HTTPS proxy',
    }
    ,
      downloads: {
        manualDocs: [
          {
            title: 'RUT241 FLYER (EN)',
            updateDate: '2024-10-23',
            type: 'flyer',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2023/08/64dde8dc736070-20865391/flyer/709173-rutm50-flyer-2023-v13.pdf'
          },
          {
            title: 'RUT241 DATASHEET (EN)',
            updateDate: '2024-11-06',
            type: 'datasheet',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2023/08/64dde8dc736070-20865391/datasheet/717015-rutm50-datasheet-2024-v10.pdf'
          }
        ],
        links: [
          {
            title: 'RUT241 QUICK START GUIDE (EN)',
            type: 'guide',
            url: 'https://wiki.teltonika-networks.com/view/QSG_RUTM50'
          },
          {
            title: 'FIRMWARE DOWNLOADS',
            type: 'firmware',
            url: 'https://wiki.teltonika-networks.com/view/RUTM50_Firmware_Downloads'
          },
          {
            title: 'RUT241 USER MANUAL',
            type: 'manual',
            url: 'https://wiki.teltonika-networks.com/view/RUTM50_Manual'
          },
      
     
          {
            title: 'CERTIFICATIONS & APPROVALS',
            type: 'certifications',
            url: 'https://wiki.teltonika-networks.com/view/RUTM50_Certification_%26_Approvals'
          },
          {
            title: 'SDK DOWNLOADS',
            type: 'sdk',
            url: 'https://wiki.teltonika-networks.com/view/Software_Development_Kit'
          },
          {
            title: 'GPL DOWNLOADS',
            type: 'gpl',
            url: 'https://wiki.teltonika-networks.com/view/OSS'
          },
          {
            title: 'RUT241 3D MODELS',
            type: '3d',
            url: 'https://wiki.teltonika-networks.com/view/RUTM50_Spatial_Measurements_%26_Weight#3D_view'
          }
        ]
      }
    }
,    
    RUT241_eSIM: {
      name: 'RUT241 eSIM',
      image: '/rut241-sim-1.png',
      images: [
        '/rut241-sim-5.png',
        '/rut241-sim-1.png',
        '/rut241-sim-2.png',
        '/rut241-sim-4.png'
      ],
      video: '/https://youtu.be/H4Qvq6ZJdNQ',
      description: 'High-performance industrial cellular router.',
      features: ['4G LTE', 'Wi-Fi', 'GPS', 'USB'],
      specifications: {
        'Mobile module': '4G LTE Cat 4 up to 150 DL/50 UL Mbps',
        '3GPP Release': 'Release 11',
        'Status': 'IME, ICCID, operator, signal strength',
        'Power': '9-30 VDC',
        'Operating Temperature': '-40°C to 75°C',
        'Dimensions': '105 x 105 x 45 mm',
        'Weight': '240g'
      },
      downloads: {
        manualDocs: [
          {
            title: 'RUT241 FLYER (EN)',
            updateDate: '2024-10-23',
            type: 'flyer',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2023/01/63b7fa877dd3c0-93872907/flyer/rut241-flyer-2023-v11.pdf'
          },
          {
            title: 'RUT241 DATASHEET (EN)',
            updateDate: '2024-11-06',
            type: 'datasheet',
            downloadUrl: 'https://teltonika-networks.com/cdn/products/2023/01/63b7fa877dd3c0-93872907/datasheet/rut241-datasheet-2024-v11.pdf'
          }
        ],
        links: [
          {
            title: 'RUT241 QUICK START GUIDE (EN)',
            type: 'guide',
            url: 'https://wiki.teltonika-networks.com/view/QSG_RUT241'
          },
          {
            title: 'FIRMWARE DOWNLOADS',
            type: 'firmware',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Firmware_Downloads'
          },
          {
            title: 'RUT241 USER MANUAL',
            type: 'manual',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Manual'
          },
          {
            title: 'PRODUCT CHANGE NOTIFICATIONS',
            type: 'certifications',
            url: 'https://wiki.teltonika-networks.com/view/Product_Change_Notifications'
          },
          {
            title: 'CERTIFICATIONS & APPROVALS',
            type: 'certifications',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Certification_%26_Approvals'
          },
          {
            title: 'SDK DOWNLOADS',
            type: 'sdk',
            url: 'https://wiki.teltonika-networks.com/view/Software_Development_Kit'
          },
          {
            title: 'GPL DOWNLOADS',
            type: 'gpl',
            url: 'https://wiki.teltonika-networks.com/view/GPL'
          },
          {
            title: 'RUT241 3D MODELS',
            type: '3d',
            url: 'https://wiki.teltonika-networks.com/view/RUT241_Spatial_Measurements_%26_Weight#3D_view'
          }
        ]
      }
    }
  };
  


  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('youtu.be')) {
      const videoId = url.split('youtu.be/')[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtube.com/watch')) {
      const videoId = new URL(url).searchParams.get('v');
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url.endsWith('.mp4') ? url : url;
  };

  const product = products[productId];

  if (!product) {
    return (
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '16px',
        width: '100%'
      }}>
        <button onClick={() => navigate('/product')} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#1e3a8a',
          marginBottom: '32px',
          cursor: 'pointer',
          border: 'none',
          background: 'none',
          fontSize: '16px'
        }}>
          ← Back to Products
        </button>
        <div style={{
          textAlign: 'center',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          padding: '32px',
          width: '100%'
        }}>
          <h2 style={{
            fontSize: 'clamp(20px, 4vw, 24px)',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '16px'
          }}>Product Not Found</h2>
          <p style={{
            color: '#4b5563',
            marginBottom: '32px'
          }}>
            Sorry, we couldn't find the product you're looking for.
            Please check the product ID or browse our available products.
          </p>
        </div>
      </div>
    );
  }

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
    setShowImageModal(true);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === product.images.length - 1 ? 0 : prev + 1
    );
    setSelectedImage(product.images[currentImageIndex === product.images.length - 1 ? 0 : currentImageIndex + 1]);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1
    );
    setSelectedImage(product.images[currentImageIndex === 0 ? product.images.length - 1 : currentImageIndex - 1]);
  };

  const VideoModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    }} onClick={() => setShowVideoModal(false)}>
      <div style={{
        backgroundColor: 'black',
        width: '100%',
        maxWidth: '1000px',
        position: 'relative',
        borderRadius: '8px',
        padding: '16px'
      }} onClick={e => e.stopPropagation()}>
        <button style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          color: 'white',
          zIndex: 1010
        }} onClick={() => setShowVideoModal(false)}>
          <X size={24} />
        </button>
        <div style={{
          width: '100%',
          aspectRatio: '16/9'
        }}>
          <iframe
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '8px'
            }}
            src={getYouTubeEmbedUrl(product.video)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );

  const ImageModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    }} onClick={() => setShowImageModal(false)}>
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }} onClick={e => e.stopPropagation()}>
        <button style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          color: 'white',
          zIndex: 1010
        }} onClick={() => setShowImageModal(false)}>
          <X size={24} />
        </button>
        
        <button style={{
          position: 'absolute',
          left: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          color: 'white',
          zIndex: 1010
        }} onClick={handlePrevImage}>
          <ChevronLeft size={32} />
        </button>
        
        <img 
          src={selectedImage} 
          alt={product.name}
          style={{
            maxHeight: '80vh',
            maxWidth: '90vw',
            objectFit: 'contain'
          }}
        />
        
        <button style={{
          position: 'absolute',
          right: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          color: 'white',
          zIndex: 1010
        }} onClick={handleNextImage}>
          <ChevronRight size={32} />
        </button>

        <div style={{
          position: 'absolute',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          padding: '16px',
          borderRadius: '8px',
          zIndex: 1010,
          overflowX: 'auto',
          maxWidth: '90vw'
        }}>
          {product.images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`${product.name} view ${index + 1}`}
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '4px',
                cursor: 'pointer',
                opacity: currentImageIndex === index ? 1 : 0.7,
                border: currentImageIndex === index ? '2px solid white' : 'none',
                flexShrink: 0
              }}
              onClick={() => {
                setSelectedImage(image);
                setCurrentImageIndex(index);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '16px',
      width: '100%'
    }}>
      <button style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#1e3a8a',
        marginBottom: '32px',
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        fontSize: '16px'
      }} onClick={() => navigate('/product')}>
        ← Back to Products
      </button>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '32px',
        width: '100%'
      }}>
        {showVideoModal && <VideoModal />}
        {showImageModal && <ImageModal />}
        
        <div>
          <img 
            src={selectedImage || product.image} 
            alt={product.name} 
            style={{
              width: '100%',
              maxHeight: '400px',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
            onClick={() => handleImageClick(selectedImage || product.image, 0)}
          />

          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            padding: '24px',
            marginTop: '32px',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              '@media (min-width: 640px)': {
                flexDirection: 'row',
                alignItems: 'center'
              }
            }}>
              <img 
                src={product.image} 
                alt={`${product.name} Quick Start Guide`} 
                style={{
                  width: '100%',
                  maxWidth: '128px',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
              <div>
                <h3 style={{
                  fontSize: 'clamp(20px, 4vw, 24px)',
                  fontWeight: 'bold',
                  color: '#1e3a8a',
                  marginBottom: '8px'
                }}>Quick Start Guide</h3>
                <h4 style={{
                  fontSize: 'clamp(16px, 3vw, 20px)',
                  fontWeight: 'bold',
                  color: '#1e3a8a'
                }}>Video Tutorial</h4>
                <div style={{
                  fontSize: 'clamp(24px, 5vw, 32px)',
                  fontWeight: 'bold',
                  color: '#1e3a8a',
                  marginTop: '16px'
                }}>{product.name}</div>
              </div>
            </div>
            <button style={{
              position: 'absolute',
              right: '24px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '64px',
              height: '64px',
              backgroundColor: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: 'none',
              cursor: 'pointer',
              '@media (max-width: 640px)': {
                position: 'relative',
                right: 'auto',
                top: 'auto',
                transform: 'none',
                marginTop: '16px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }
            }} onClick={() => setShowVideoModal(true)}>
              <Play size={24} color="#1e3a8a" />
            </button>
          </div>
        </div>

        <div>
          <section>
            <h2 style={{
              fontSize: 'clamp(18px, 4vw, 20px)',
              fontWeight: 'bold',
              color: '#1e3a8a',
              marginBottom: '24px'
            }}>PRODUCT GALLERY</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                backgroundColor: 'black',
                borderRadius: '8px',
                padding: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }} onClick={() => handleImageClick(product.image, 0)}>
                <img 
                  src={product.image} 
                  alt={product.name} 
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </div>
              <div style={{
                backgroundColor: 'black',
                borderRadius: '8px',
                padding: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }} onClick={() => {
                setSelectedImage(product.images[0]);
                setCurrentImageIndex(0);
                setShowImageModal(true);
              }}>
                <p style={{
                  color: 'white',
                  fontWeight: 'bold'
                }}>MORE PHOTOS</p>
              </div>
            </div>
          </section>

          <div style={{
            borderBottom: '1px solid #e2e8f0',
            marginTop: '32px',
            overflowX: 'auto'
          }}>
            <nav style={{
              display: 'flex',
              gap: '16px',
              whiteSpace: 'nowrap',
              padding: '0 0 8px 0'
            }}>
              {['FEATURES', 'DOWNLOADS', 'ORDERING', 'SUPPORT'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab.toLowerCase())}
                  style={{
                    paddingBottom: '8px',
                    position: 'relative',
                    cursor: 'pointer',
                    color: activeTab === tab.toLowerCase() ? '#1e3a8a' : '#94a3b8',
                    fontWeight: '500',
                    border: 'none',
                    background: 'none',
                    fontSize: 'clamp(14px, 2vw, 16px)'
                  }}
                >
                  {tab}
                  {activeTab === tab.toLowerCase() && (
                    <div style={{
                      position: 'absolute',
                      bottom: '-1px',
                      left: 0,
                      right: 0,
                      height: '2px',
                      backgroundColor: '#1e3a8a'
                    }} />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'ordering' && (
            <div style={{
              marginTop: '24px'
            }}>
              <OrderingTab productId={productId} />
            </div>
          )}

          {activeTab === 'support' && (
            <div style={{
              marginTop: '24px'
            }}>
              <SupportTab />
            </div>
          )}
   {activeTab === 'features' && (
        <div style={{ marginTop: '24px' }}>
          <section>
            <h3 style={{
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '16px',
              fontSize: 'clamp(16px, 3vw, 18px)'
            }}>MOBILE</h3>
            <div style={{
              maxHeight: isExpanded ? 'none' : '300px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {Object.entries(product.specifications).map(([key, value]) => (
                <div key={key} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: '16px',
                  '@media (min-width: 640px)': {
                    flexDirection: 'row'
                  }
                }}>
                  <span style={{
                    color: '#4b5563',
                    width: '100%',
                    '@media (min-width: 640px)': {
                      width: '33.333%'
                    }
                  }}>{key}</span>
                  <span style={{
                    color: '#1f2937',
                    width: '100%',
                    '@media (min-width: 640px)': {
                      width: '66.666%'
                    }
                  }}>{value}</span>
                </div>
              ))}
              {!isExpanded && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '100px',
                  background: 'linear-gradient(transparent, white)',
                  pointerEvents: 'none'
                }} />
              )}
            </div>
            <button
              style={{
                width: 'auto',
                minWidth: '150px',
                backgroundColor: '#1e3a8a',
                color: 'white',
                padding: '8px 24px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                marginTop: '24px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '24px auto 0'
              }}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <>
                  Show Less <ChevronUp size={20} />
                </>
              ) : (
                <>
                  Show More <ChevronDown size={20} />
                </>
              )}
            </button>
          </section>
        </div>
      )}
{activeTab === 'downloads' && (
  <div style={{
    padding: '16px',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box'
  }}>
    <div style={{
      marginTop: '24px'
    }}>
      <div style={{
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '20px'
      }}>
        MANUAL & DOCUMENTATION
      </div>
      <div style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        {product.downloads.manualDocs.map((doc, index) => (
          <a 
            key={index}
            href={doc.downloadUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '20px',
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
              ':hover': {
                backgroundColor: '#e8e8e8'
              }
            }}>
              <div style={{
                marginRight: '16px',
                marginBottom: window.innerWidth <= 768 ? '8px' : 0,
                color: '#0066cc'
              }}>
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </div>
              <div style={{
                flex: 1
              }}>
                <div style={{
                  fontSize: '14px',
                  color: '#333',
                  textDecoration: 'none',
                  display: 'block',
                  marginBottom: '4px'
                }}>
                  {doc.title}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>
                  Updated at {doc.updateDate}
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>

    <div style={{
      marginTop: '32px'
    }}>
      <div style={{
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '20px'
      }}>
        LINKS
      </div>
      <div style={{
        listStyle: 'none',
        padding: 0,
        margin: '0 0 40px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {product.downloads.links.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              borderRadius: '6px',
              transition: 'background-color 0.2s',
              ':hover': {
                backgroundColor: '#f5f5f5'
              }
            }}>
              <span style={{
                marginRight: '8px',
                display: 'flex',
                alignItems: 'center',
                color: '#0066cc'
              }}>
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </span>
              <span style={{
                fontSize: '14px',
                color: '#333'
              }}>
                {link.title}
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  </div>
)}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;